/**
 * Shared persistence for Orders / Order Validation page filter selections.
 * Survives manual refresh, auto-refresh, browser refresh, and remount.
 */

export const ORDERS_FILTER_STATE_STORAGE_KEY = 'ecommerce-orders-filter-state-v1';
export const ORDER_VALIDATION_FILTER_STATE_STORAGE_KEY = 'ecommerce-order-validation-filter-state-v1';

export const DEFAULT_ORDERS_FILTER_STATE = {
    weekFilter: false,
    monthFilter: false,
    assembliesFilter: false,
    kpiQuickFilter: null,
    searchTerm: '',
    startDate: '',
    endDate: '',
    orderValue: '',
    POValue: '',
    PartNameValue: '',
    ShippingMethodValue: '',
    CountryValue: '',
    CustomerValue: '',
    customerId: '',
    WOValue: '',
    statusValue: '',
    viewBy: false,
    selectedOrderStatusTags: [],
    selectedLineStatusTags: [],
};

function normalizeKpiQuickFilter(value) {
    if (value == null) return null;
    if (value === 'due' || value === 'missing' || value === 'ready') return value;
    if (
        typeof value === 'object' &&
        (value.kind === 'order' || value.kind === 'line') &&
        typeof value.label === 'string' &&
        value.label.trim()
    ) {
        return { kind: value.kind, label: value.label.trim() };
    }
    return null;
}

function normalizePersistedTagList(raw) {
    if (!Array.isArray(raw)) return [];
    return raw
        .filter((t) => t && typeof t === 'object' && typeof t.label === 'string' && t.label.trim())
        .map((t) => {
            const tag = { label: t.label.trim() };
            if (typeof t.color === 'string') tag.color = t.color;
            if (typeof t.icon === 'string') tag.icon = t.icon;
            return tag;
        });
}

export function normalizeOrdersFilterState(raw) {
    if (!raw || typeof raw !== 'object') {
        return { ...DEFAULT_ORDERS_FILTER_STATE };
    }
    let weekFilter = !!raw.weekFilter;
    let monthFilter = !!raw.monthFilter;
    // Week and month are mutually exclusive; prefer week if both were saved.
    if (weekFilter && monthFilter) {
        monthFilter = false;
    }
    return {
        weekFilter,
        monthFilter,
        assembliesFilter: !!raw.assembliesFilter,
        kpiQuickFilter: normalizeKpiQuickFilter(raw.kpiQuickFilter),
        searchTerm: typeof raw.searchTerm === 'string' ? raw.searchTerm : '',
        startDate: typeof raw.startDate === 'string' ? raw.startDate : '',
        endDate: typeof raw.endDate === 'string' ? raw.endDate : '',
        orderValue: typeof raw.orderValue === 'string' ? raw.orderValue : '',
        POValue: typeof raw.POValue === 'string' ? raw.POValue : '',
        PartNameValue: typeof raw.PartNameValue === 'string' ? raw.PartNameValue : '',
        ShippingMethodValue: typeof raw.ShippingMethodValue === 'string' ? raw.ShippingMethodValue : '',
        CountryValue: typeof raw.CountryValue === 'string' ? raw.CountryValue : '',
        CustomerValue: typeof raw.CustomerValue === 'string' ? raw.CustomerValue : '',
        customerId: raw.customerId != null && raw.customerId !== '' ? String(raw.customerId) : '',
        WOValue: typeof raw.WOValue === 'string' ? raw.WOValue : '',
        statusValue: typeof raw.statusValue === 'string' ? raw.statusValue : '',
        viewBy: !!raw.viewBy,
        selectedOrderStatusTags: normalizePersistedTagList(raw.selectedOrderStatusTags),
        selectedLineStatusTags: normalizePersistedTagList(raw.selectedLineStatusTags),
    };
}

export function loadOrdersFilterState(storageKey = ORDERS_FILTER_STATE_STORAGE_KEY) {
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return { ...DEFAULT_ORDERS_FILTER_STATE };
        return normalizeOrdersFilterState(JSON.parse(raw));
    } catch (e) {
        return { ...DEFAULT_ORDERS_FILTER_STATE };
    }
}

export function saveOrdersFilterState(state, storageKey = ORDERS_FILTER_STATE_STORAGE_KEY) {
    try {
        localStorage.setItem(storageKey, JSON.stringify(normalizeOrdersFilterState(state)));
    } catch (e) {
        /* ignore quota / private mode */
    }
}

export function clearOrdersFilterState(storageKey = ORDERS_FILTER_STATE_STORAGE_KEY) {
    try {
        localStorage.removeItem(storageKey);
    } catch (e) {
        /* ignore */
    }
}

export function hasActiveOrdersFilter(state) {
    if (!state) return false;
    return !!(
        state.weekFilter ||
        state.monthFilter ||
        state.assembliesFilter ||
        state.kpiQuickFilter != null ||
        state.searchTerm ||
        state.startDate ||
        state.endDate ||
        state.orderValue ||
        state.POValue ||
        state.PartNameValue ||
        state.ShippingMethodValue ||
        state.CountryValue ||
        state.CustomerValue ||
        state.WOValue ||
        state.statusValue ||
        state.viewBy ||
        (state.selectedOrderStatusTags && state.selectedOrderStatusTags.length > 0) ||
        (state.selectedLineStatusTags && state.selectedLineStatusTags.length > 0)
    );
}
